/* Who may open an account, and when a parent or guardian must agree.
   - Under 13: no account. US COPPA requires verifiable parental consent to collect data from children under 13;
     the product spec chose 13+ as the floor instead.
   - 13 to 17: a parent or guardian must consent before the student stores a profile. This one rule covers the
     strictest of our markets (India's DPDP Act requires verifiable parental consent under 18; the GDPR's digital-
     consent age is 13 to 16 depending on the country).
   - 18 and over: the student consents for themselves.
   This is the conservative default. A lawyer should confirm it (see docs/COMPLIANCE.md). */
export const MIN_AGE = 13;
export const ADULT_AGE = 18;

export function ageOn(dobISO: string, todayISO: string): number {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dobISO), t = /^(\d{4})-(\d{2})-(\d{2})$/.exec(todayISO);
  if (!d || !t) throw new Error('dates must be YYYY-MM-DD');
  const [dy, dm, dd] = d.slice(1).map(Number), [ty, tm, td] = t.slice(1).map(Number);
  /* a real calendar date only: 2000-02-31 or 2001-02-29 are refused, 2000-02-29 is fine */
  const real = (y: number, m: number, day: number) => { const x = new Date(Date.UTC(y, m - 1, day)); return x.getUTCFullYear() === y && x.getUTCMonth() === m - 1 && x.getUTCDate() === day; };
  if (!real(dy, dm, dd)) throw new Error('invalid date of birth');
  if (!real(ty, tm, td)) throw new Error('invalid date');
  let age = ty - dy;
  if (tm < dm || (tm === dm && td < dd)) age -= 1;
  return age;
}

export type AgeDecision =
  | { allowed: false; guardianRequired: false; reason: 'under_min' | 'invalid' }
  | { allowed: true; guardianRequired: true; reason: 'minor' }
  | { allowed: true; guardianRequired: false; reason: 'adult' };

export function ageDecision(dobISO: string, todayISO: string): AgeDecision {
  let age: number;
  try { age = ageOn(dobISO, todayISO); } catch { return { allowed: false, guardianRequired: false, reason: 'invalid' }; }
  if (age < 0 || age > 120) return { allowed: false, guardianRequired: false, reason: 'invalid' };
  if (age < MIN_AGE) return { allowed: false, guardianRequired: false, reason: 'under_min' };
  if (age < ADULT_AGE) return { allowed: true, guardianRequired: true, reason: 'minor' };
  return { allowed: true, guardianRequired: false, reason: 'adult' };
}

/* a minor's account stays limited (no profile, documents or payments) until the guardian has consented */
export const canStoreProfile = (d: AgeDecision, guardianConsented: boolean) => d.allowed && (!d.guardianRequired || guardianConsented);
